/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import CommonAlertCard from "../../../../components/_utils/CommonAlertCard/index.jsx";
import PrimaryWithIconButton from "../../../../components/_utils/CommonButtons/PrimaryWithIconButton";
import CommonFileUpload from "../../../../components/_utils/CommonFileUpload";
import SmallPopupWithBulletPoints from "../../../../components/_utils/CommonSmallPopupWithBulletPoint";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import CommonToast from "../../../../components/_utils/CommonToast";
import SVGComponent from "../../../../components/_utils/SVGComponent";
import CategorySelector from "../../../../components/_utils/TransactionCommonComponents/CategorySelector";
import Loader from "../../../../components/Loader/Index";
import useServiceChecker from "../../../../components/ServiceChecker";
import { uploadFile } from "../../../../redux/features/sharedResources/sharedResources";
import {
  bulkTransactionDownloadSampleFile,
  bulkTransactionUpload,
} from "../../../../redux/features/transactions/beneficiaryTransactions";
import {
  getOutsideBankingHoursLabel,
  isOutsideBankingHours,
} from "../../../../utils/bankingHours";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";

function BulkTrasanctionCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ensureAll] = useServiceChecker();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(null);
  const [removeClassName, setRemoveClassName] = useState(true);
  const [disabledLoading, setDisabledLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showBankingHoursPopup, setShowBankingHoursPopup] = useState(false);
  const isOutsideBankingHoursNow = isOutsideBankingHours();
  const shouldShowBankingHoursAlert =
    isOutsideBankingHoursNow &&
    activeStep === GlobalConfiguration.lengthCheckZero;

  const steps = ["Transaction category", "Upload transaction file"];

  const categories = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );

  const activeCategories = useMemo(() => {
    return categories?.filter((c) => c.active === "Y").map((c) => c.id) || [];
  }, [categories]);

  const isFileAlreadyUploaded =
    files?.length !== GlobalConfiguration.lengthCheckZero;
  const isFileSelected = files?.length !== GlobalConfiguration.lengthCheckZero;

  const baseStepConfig = {
    previousEnableBgColor: "var(--primary-nav-act)",
    previousDisableBgColor: "var(--text-disabled)",
    previousEnableTextColor: "var(--text-primary)",
    previousHoverOption: "white",
    nextDisableBgColor: "var(--text-disabled)",
    nextDisableTextColor: "var(--text-primary)",
    nextHoverOption: "black",
  };

  const dataArrayBulk = [
    {
      ...baseStepConfig,
      previousText: "Cancel",
      nextText: "Next",
      nextEnableBgColor: "var(--text-primary)",
      nextEnableTextColor: "var(--primary-bg)",
      nextIsEnabled: !!selectedCategory,
      nextIsLoading: false,
      stepperText: steps[0],
      extraInfoSubText:
        "Select a category to generate its corresponding transaction file. Avoid categories with file uploads, as attachments are not supported in .csv files.",
      extraInfoShow: true,
    },
    {
      ...baseStepConfig,
      previousText: "Previous",
      nextText: "Upload",
      nextEnableBgColor: "var(--positive-act)",
      nextEnableTextColor: "var(--text-primary)",
      nextIsEnabled: isFileSelected,
      nextIsLoading: disabledLoading,
      stepperText: steps[1],
      extraInfoShow: false,
    },
  ];

  const handleUpdate = async () => {
    if (disabledLoading) return;
    setDisabledLoading(true);
    setLoading(true);

    try {
      const fileResponse = await dispatch(uploadFile({ file: files }));

      if (fileResponse?.error) {
        CommonToast({
          text: "File upload failed",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
        return;
      }

      const fileData = fileResponse?.payload;
      let statementPayload = {
        uploaded_file_data: {
          id: fileData?.id,
          storage: "cache",
          metadata: {
            filename: fileData?.metadata?.filename,
            size: fileData?.metadata?.size,
            mime_type: fileData?.metadata?.mime_type,
          },
        },
      };

      let response;
      statementPayload = {
        ...statementPayload,
        category_id: selectedCategory?.id,
        category_name: selectedCategory?.name,
      };

      response = await dispatch(bulkTransactionUpload({ statementPayload }));

      const data = response?.payload;

      if (data?.status === true) {
        navigate(
          "/beneficiary_transaction_dashboard/create_bulk_transaction/success",
          {
            state: { files, requestReferenceId: data?.request_reference_id },
          },
        );
      } else {
        setFiles([]);
        CommonToast({
          text:
            data?.errors?.user_unauthorized?.[0] ||
            data?.errors?.base?.[0] ||
            data?.error,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
        navigate(
          "/beneficiary_transaction_dashboard/create_bulk_transaction/error",
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setDisabledLoading(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      CommonToast({
        text: "Downloading sample file.",
        icon: ICONS?.LOADER_GIF,
        variant: "var(--processing)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
      await dispatch(
        bulkTransactionDownloadSampleFile({
          category_id: selectedCategory?.id,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setFiles([]);
    setSelectedCategory(null);
    setActiveStep(0);
  }, []);

  useEffect(() => {
    if (direction) {
      const timer = setTimeout(() => {
        setRemoveClassName(true);
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [activeStep]);

  useEffect(() => {
    let dragCounter = GlobalConfiguration?.lengthCheckZero;

    const handleDragEnter = (e) => {
      e.preventDefault();
      dragCounter++;
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter === GlobalConfiguration?.lengthCheckZero) {
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      dragCounter = GlobalConfiguration?.lengthCheckZero;
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  const animationClassS =
    direction === "next" ? "animate-slide-next" : "animate-slide-prev";

  useEffect(() => {
    if (direction) {
      const timer = setTimeout(() => {
        setRemoveClassName(true);
      }, IntervalConfiguration.intervalSmaller);
      return () => clearTimeout(timer);
    }
  }, [activeStep]);

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <div className="w-width-full hidden Qsmd:block h-navbar-breadcrumb-height Qlg:bg-primary-bg bg-primary-nav flex-shrink-0"></div>
      {showBankingHoursPopup && (
        <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop bg-opacity-dark-opacity justify-center z-z-index-modal">
          <SmallPopupWithBulletPoints
            headerTitle="Why transaction cannot be processed?"
            headerFontWeight="semibold"
            headerFontColor="negative-act"
            textSize="font-md"
            bulletPoints={[
              `Any transaction created or approved after banking hours i.e. between ${getOutsideBankingHoursLabel()} will be processed on the next bank working day.`,
              "Payments are time-critical (e.g., salary, vendor payment, or bill payment), consider the possible processing delay before proceeding.",
            ]}
            closeIconImage={ICONS?.CLOSE}
            closeModal={() => setShowBankingHoursPopup(false)}
          />
        </div>
      )}
      {loading && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-fixed-ui bg-PopUp-BackDrop opacity-dark-opacity">
          <div>
            <Loader type={"bulk_upload"} />
            <p className="font-semibold text-font-lg text-text-primary">
              Uploading file
            </p>
          </div>
        </div>
      )}
      <div className="fixed bottom-bottom-base w-width-full Qmd:hidden">
        <div
          style={{
            display: activeStep === 1 && "none",
          }}
          className="Refactor_responsive_Bottomdiv w-width-full bg-primary-nav-act p-padding-md relative global-hover-white z-z-index-base cursor-pointer"
        >
          <div className="flex justify-between">
            <p className="text-text-primary text-font-xs">
              Select a category to generate its corresponding transaction file.
              Avoid categories with file uploads, as attachments are not
              supported in .csv files.
            </p>
          </div>
        </div>
        <div className="h-stepper-moble-height w-width-full block Qmd:hidden flex-shrink bg-primary-nav-act"></div>
      </div>
      <div className="relative flex flex-col flex-1 overflow-hidden w-width-full">
        <div className="flex items-center w-full-width h-mobile-create-header Qsmd:hidden bg-primary-nav px-padding-md py-padding-sm top-top-base z-z-index-header shadow-box-shadow-bottom rounded-b-border-radius-md">
          <div
            className="cursor-pointer"
            onClick={() => {
              navigate("/beneficiary_transaction_dashboard");
            }}
          >
            <SVGComponent
              url={ICONS?.ARROW_LEFT_WHITE}
              color={"var(--text-primary)"}
              height={"var(--icon-sm)"}
              width={"var(--icon-sm)"}
              alt="Arrow"
            />
          </div>
          <div className="flex flex-col gap-gap-xs ml-margin-md">
            <div className="text-text-primary text-base">
              Create bulk transaction
            </div>
            <div className="text-text-secondary text-xs">
              {steps[activeStep]}
            </div>
          </div>
        </div>
        <div
          className={`flex justify-center items-end !bottom-bottom-base rounded-t-border-radius-md rounded-b-border-radius-md w-screen fixed Qsmd:relative Qsmd:top-top-base left-left-base z-z-index-header flex-shrink-0  ${activeStep === GlobalConfiguration?.lengthCheckZero ? "h-[calc(var(--padding-bottom-unassign-cat)+var(--pagination-height))]" : "h-[var(--padding-bottom-unassign-cat)]"}`}
        >
          <PrimaryStepper
            activeStep={activeStep}
            handleForwardAction={() => {
              if (activeStep === GlobalConfiguration?.lengthCheckZero) {
                setDirection("next");
                setRemoveClassName(false);

                if (!selectedCategory) return;
                setActiveStep(1);
              } else {
                if (files?.length === GlobalConfiguration?.lengthCheckZero)
                  return;
                handleUpdate();
              }
            }}
            handleBackwardAction={() => {
              setDirection("prev");
              setRemoveClassName(false);
              if (activeStep === GlobalConfiguration?.lengthCheckZero) {
                navigate(-1);
              } else {
                setActiveStep(0);
              }
            }}
            dataArray={dataArrayBulk}
          />
        </div>
        <div
          className={`h-full w-width-full overflow-y-auto flex-1 mx-auto Qlg:px-padding-base px-padding-md ${activeStep === GlobalConfiguration?.lengthCheckZero ? " pt-[calc(var(--padding-md)+var(--borderthickness))] pb-padding-md" : "pt-padding-md"} ${
            activeStep === GlobalConfiguration.lengthCheckZero &&
            direction === "next"
              ? ""
              : removeClassName === true
                ? ""
                : animationClassS
          }`}
        >
          {shouldShowBankingHoursAlert && (
            <div className="max-w-tab-width-normal Qlg:mx-auto  Qlg:px-padding-base w-width-full mb-margin-md">
              <CommonAlertCard
                title="Functioning outside banking hours"
                texts="Transactions will be processed automatically during the next bank working day. Learn more"
                highlightedText="Learn more"
                onHighlightedTextClick={() => setShowBankingHoursPopup(true)}
              />
            </div>
          )}
          {activeStep === GlobalConfiguration?.lengthCheckZero && (
            <div className="max-w-tab-width-normal mx-auto flex flex-col Qlg:flex-row Qlg:gap-gap-xl gap-gap-md">
              <div
                className={`rounded-border-radius-md w-width-full bg-primary-card lg:max-w-[--create-box-width] h-[fit-content] ${isFileAlreadyUploaded ? "opacity-light-opacity pointer-events-none" : ""}`}
              >
                <CategorySelector
                  previewMode={false}
                  category={activeCategories}
                  selectedCategory={selectedCategory}
                  unassignCategories={[]}
                  unassignlength={[]}
                  disableCategories={false}
                  enableSearch={true}
                  showUnassignCTA={true}
                  handleCategorySelect={(id) => {
                    const fullObj = categories.find((c) => c.id === id);
                    setSelectedCategory(fullObj);
                  }}
                  hideSubHeader={true}
                />
              </div>
              {isFileAlreadyUploaded && (
                <div className="bg-emphasis rounded-border-radius-md p-padding-md flex items-center gap-gap-sm h-[max-content] w-full">
                  <img
                    src={ICONS?.INFO_FILLED}
                    alt=""
                    className="w-icon-md h-icon-md flex-shrink-0"
                  />
                  <p className="text-text-primary text-font-sm">
                    Remove the uploaded file to enable category change.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeStep === GlobalConfiguration?.lengthCheckOne && (
            <>
              <div className="max-w-tab-width-normal mx-auto flex flex-col Qlg:flex-row Qlg:gap-gap-xl gap-gap-md">
                <div className="relative rounded-border-radius-md w-width-full bg-primary-card Qlg:max-w-[--create-box-width] h-[fit-content]">
                  <div className="bg-primary-card p-padding-md rounded-border-radius-md">
                    <div className="flex flex-col gap-gap-sm">
                      <div className="flex flex-col gap-gap-sm text-text-primary">
                        <p className="font-semibold text-font-md">
                          Sample File
                        </p>
                        <div className="flex flex-col gap-gap-sm">
                          <p className="text-font-xs font-normal">Note:</p>
                          <ul className="text-font-xs font-normal list-disc pl-padding-md">
                            <li>
                              For a successful transaction upload:
                              <ul className="list-none pl-padding-xs">
                                <li>
                                  - Make sure to follow the sample file format.
                                </li>
                                <li>
                                  - Debit account must exist on Xpentra, do not
                                  enter new ones.
                                </li>
                              </ul>
                            </li>
                            <li>
                              New beneficiary accounts identified in the
                              uploaded file will be created and added to the
                              pending accounts.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="flex flex-col gap-gap-sm text-text-primary">
                        <div className="border-b border-primary-bg" />
                        <p className="text-font-xs font-normal">
                          Recommended steps:
                        </p>
                        <ul className="text-font-xs font-normal list-disc pl-padding-md">
                          <li>Download and open the sample file.</li>
                          <li>
                            Select the{" "}
                            <span className="font-semibold">
                              Debit Account Number
                            </span>{" "}
                            and{" "}
                            <span className="font-semibold">
                              Credit Account Number columns
                            </span>
                            .
                          </li>
                          <li>
                            Set the Data type of these columns to{" "}
                            <span className="font-semibold">Text</span> .
                          </li>
                          <li>
                            Enter the required data and upload the file to
                            Xpentra.
                          </li>
                        </ul>
                      </div>
                      <div className="flex justify-end text-font-xs font-normal text-text-primary">
                        <PrimaryWithIconButton
                          btnText="Sample file"
                          btnType="button"
                          icon={ICONS?.DOWNLOAD_STATEMENT}
                          iconToBeAlign="left"
                          hoverOption="black"
                          fullWidthRequired={false}
                          inActiveVariantNeeded={false}
                          enabledTextColor="var(--text-primary)"
                          disabledTextColor="var(--text-primary)"
                          enabledBgColor="var(--emphasis)"
                          disabledBgColor="var(--emphasis)"
                          isDisabled={
                            !selectedCategory?.id ||
                            !ensureAll(["VTransactionUpload"]).status
                          }
                          handleFunction={() => {
                            const serviceCheck = ensureAll([
                              "VTransactionUpload",
                            ]);
                            if (serviceCheck.status) {
                              if (selectedCategory?.id) handleDownloadSample();
                            } else {
                              navigate("/feature_not_assigned", {
                                state: serviceCheck.unavailableService,
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-auto media-password w-width-md bg-primary-card rounded-border-radius-md mb-margin-md Qlg:mb-margin-base p-padding-md">
                  <p className="font-sans font-semibold text-font-md text-text-primary">
                    Upload File
                  </p>
                  <div className="mt-margin-md">
                    <CommonFileUpload
                      files={files}
                      setFiles={setFiles}
                      type="custom"
                      customExtensions={[".csv"]}
                    />
                  </div>

                  <div className="flex flex-col gap-gap-sm mt-margin-md text-text-primary">
                    <span className="font-sans font-normal text-font-xs">
                      File verification process post upload:
                    </span>
                    <ul className="text-font-xs font-normal list-disc pl-padding-md">
                      <li>
                        Our system validates the values in each row. If any
                        errors are identified, the entire file will get
                        rejected.
                      </li>
                      <li>
                        Upon successful validation, you will be notified by
                        email, and the corresponding transactions will be
                        created and sent for approval.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div
          className={` ${activeStep === GlobalConfiguration?.lengthCheckZero ? "h-[calc(var(--navbar-height)+var(--stepper-moble-height))]" : "h-stepper-moble-height"} flex Qsmd:hidden`}
        ></div>
      </div>
    </div>
  );
}

export default BulkTrasanctionCreate;
